import { PricingErrorDto } from '@/features/public/booking/types/BookingQuoteDto.ts';
import { PricingErrorType } from '@/features/public/booking/types/PricingErrorType.ts';
import { getPricingErrorSentence } from '@/features/public/booking/types/PricingErrorType.utils.ts';

/**
 * Opcja, której kafelek stoi na ekranie. `targetId` błędu wskazuje opcję dla
 * większości kodów i wariant dla `OPTION_VARIANT_FULL`, więc do rozpoznania
 * adresata trzeba obu list.
 */
export interface PricingErrorOptionTarget {
  optionId: string;
  variantIds: string[];
}

export interface PricingErrorPlacementContext {
  options: PricingErrorOptionTarget[];
  /** Czy uczestnik ma na ekranie wybór transportu (wyjazd bez transportu go nie ma). */
  hasTransportSelector: boolean;
}

/** Komunikaty wyceny rozłożone po polach formularza; wszystko to gotowe zdania. */
export interface PlacedPricingErrors {
  byOptionId: Record<string, string[]>;
  transport: string[];
  birthDate: string[];
  /** `TOUR_FULL` i to, czego nie dało się przypiąć do żadnej kontrolki. */
  participant: string[];
}

/**
 * „Wymagane, a jeszcze niewybrane" nie jest faktem o dostępności, tylko
 * walidacją — a tę na kroku 1 prowadzi Zod przy wysyłce, pod tą samą kontrolką.
 * Wycena leci przy każdej zmianie formularza, więc bez tego wyjątku klient
 * dostawałby czerwony komunikat w sekundzie, w której opcje w ogóle się
 * pokazały.
 */
const validationOwnedByZod: ReadonlySet<PricingErrorType> = new Set([
  PricingErrorType.REQUIRED_OPTION_NOT_SELECTED,
  PricingErrorType.TRANSPORT_SELECTION_REQUIRED,
]);

const optionCodes: ReadonlySet<PricingErrorType> = new Set([
  PricingErrorType.OPTION_VARIANT_NOT_FOUND,
  PricingErrorType.OPTION_PRICE_NOT_FOUND,
  PricingErrorType.OPTION_PRICE_NOT_AVAILABLE_FOR_AGE,
  PricingErrorType.OPTION_MULTI_SELECT_NOT_ALLOWED,
  PricingErrorType.OPTION_ARCHIVED,
  PricingErrorType.OPTION_VARIANT_ARCHIVED,
  PricingErrorType.OPTION_PRICE_ARCHIVED,
  PricingErrorType.OPTION_FULL,
  PricingErrorType.OPTION_VARIANT_FULL,
]);

const transportCodes: ReadonlySet<PricingErrorType> = new Set([
  PricingErrorType.TRANSPORT_NOT_OFFERED,
  PricingErrorType.TRANSPORT_VARIANT_NOT_FOUND,
  PricingErrorType.TRANSPORT_AGE_NOT_ELIGIBLE,
  PricingErrorType.TRANSPORT_VARIANT_SOLD_OUT,
]);

/** Błędy, które klient zobaczy w formularzu — a więc i te, które mogą go zatrzymać. */
export const visiblePricingErrors = (
  errors: PricingErrorDto[] | undefined,
): PricingErrorDto[] =>
  (errors ?? []).filter((error) => !validationOwnedByZod.has(error.code));

export const hasVisiblePricingErrors = (
  errors: PricingErrorDto[] | undefined,
): boolean => visiblePricingErrors(errors).length > 0;

const findOptionId = (
  options: PricingErrorOptionTarget[],
  targetId: string | null,
): string | undefined => {
  if (!targetId) return undefined;
  return options.find(
    (option) =>
      option.optionId === targetId || option.variantIds.includes(targetId),
  )?.optionId;
};

const pushOnce = (messages: string[], message: string) => {
  if (!messages.includes(message)) messages.push(message);
};

/**
 * Błędy wyceny jednego uczestnika rozłożone tam, gdzie klient może na nie
 * zareagować. Adresata, którego nie ma na ekranie (opcja zarchiwizowana, więc
 * jej kafelek już się nie renderuje), zbiera karta uczestnika — inaczej
 * rezerwacja blokowałaby się bez słowa wyjaśnienia.
 */
export const placePricingErrors = (
  errors: PricingErrorDto[] | undefined,
  context: PricingErrorPlacementContext,
): PlacedPricingErrors => {
  const placed: PlacedPricingErrors = {
    byOptionId: {},
    transport: [],
    birthDate: [],
    participant: [],
  };

  for (const error of visiblePricingErrors(errors)) {
    const sentence = getPricingErrorSentence(error.code);

    if (
      error.code ===
      PricingErrorType.NO_BASE_PRICE_AVAILABLE_FOR_PARTICIPANT_WITH_AGE
    ) {
      pushOnce(placed.birthDate, sentence);
      continue;
    }

    if (transportCodes.has(error.code)) {
      pushOnce(
        context.hasTransportSelector ? placed.transport : placed.participant,
        sentence,
      );
      continue;
    }

    if (optionCodes.has(error.code)) {
      const optionId = findOptionId(context.options, error.targetId);
      if (optionId) {
        placed.byOptionId[optionId] ??= [];
        pushOnce(placed.byOptionId[optionId], sentence);
      } else {
        pushOnce(placed.participant, sentence);
      }
      continue;
    }

    pushOnce(placed.participant, sentence);
  }

  return placed;
};
